import { createSlice } from "@reduxjs/toolkit";

import {
  getQuotationThunk,
  createQuotationThunk,
  updateQuotationThunk,
  deleteQuotationThunk,
  updateStatusQuotationThunk,
} from "state/action/quotation";

// Helper function untuk menentukan struktur 'details'
const getDetailsStructure = (itemType) => {
  switch (itemType) {
    case "Additional Service":
      return {
        service: "",
        item: "",
      };
    case "Intangible Rights":
      return {
        asset_name: "",
        station_code: "",
        contract_period_start: "",
        contract_period_end: "",
      };
    case "Unit":
      return {
        property: "",
        building: "",
        floor: "",
        unit: "",
        fitting_in: "",
        fitting_out: "",
        initial_handover: "",
        contract_start: "",
        contract_end: "",
      };
    case "One Time Fee":
      return {
        scope: "",
      };
    case "Casual Lease":
      return {
        location: "",
        event_dates: "",
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

// Initial state
const initialState = {
  // Get Quotation inisial state
  getQuotation: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create Quotation inisial state
  createQuotation: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update Quotation inisial state
  updateQuotation: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  updateStatusQuotation: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Quotation inisial state
  deleteQuotation: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Quotation object value
  quotation_object_value: "",
  createQuotationInputBody: {
    quotation_id: "",
    title: "",
    version: 1,
    status: "Draft",
    issue_date: new Date(),
    valid_until_date: null,
    start_date: null,
    client: "",
    sales_person: "",
    department: "",
    items: [
      {
        item_type: "",
        description: "",
        quantity: 1,
        unit: "unit",
        unit_price: "",
        total_price: "",
        details: "",
      },
    ],
    summary: {
      subtotal: 0,
      discounts: [],
      taxes: [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: 0,
        },
      ],
      grand_total: 0,
      currency: "IDR",
    },
    terms: {
      payment_schedule: [
        {
          description: "Pembayaran Penuh",
          percentage: 100,
          amount: 0,
          due_date: null,
        },
      ],
      scope_of_work: "",
      general_conditions: "",
    },
    notes: "",
    attachments: [],
  },
  selectedQuotation: {
    quotation_id: "",
    title: "",
    version: 1,
    status: "Draft",
    issue_date: new Date(),
    valid_until_date: null,
    start_date: null,
    client: "",
    sales_person: "",
    department: "",
    items: [
      {
        item_type: "",
        description: "",
        quantity: 1,
        unit: "unit",
        unit_price: "",
        total_price: "",
        details: {},
      },
    ],
    summary: {
      subtotal: 0,
      discounts: [],
      taxes: [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: 0,
        },
      ],
      grand_total: 0,
      currency: "IDR",
    },
    terms: {
      payment_schedule: [
        {
          description: "Pembayaran Penuh",
          percentage: 100,
          amount: 0,
          due_date: null,
        },
      ],
      scope_of_work: "",
      general_conditions: "",
    },
    notes: "",
    attachments: [],
  },
  editQuotationInputBody: {
    status: "",
  },
};

export const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    setGetQuotationObjectValue: (state, action) => {
      return {
        ...state,
        quotation_object_value: action.payload,
      };
    },

    // TAMBAHKAN REDUCER BARU INI
    updateQuotationSummary: (state, action) => {
      const { subtotal, ppnAmount } = action.payload;
      const summary = state.createQuotationInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // INI BAGIAN PENTINGNYA: Action untuk mengubah tipe item
    updateItemType: (state, action) => {
      // Payload harus berisi index item dan tipe barunya
      const { itemIndex, newType } = action.payload;

      // Ambil item yang akan diubah
      const itemToUpdate = state.createQuotationInputBody.items[itemIndex];

      if (itemToUpdate) {
        // Update item_type
        itemToUpdate.item_type = newType;
        // Ganti keseluruhan objek 'details' dengan struktur baru
        itemToUpdate.details = getDetailsStructure(newType);
      }
    },

    updateItem: (state, action) => {
      const { index, field, value } = action.payload;
      const items = state.createQuotationInputBody.items;

      // 1. Update nilai field pada item yang spesifik
      if (items[index]) {
        items[index][field] = value;
      }

      // 2. Hitung ulang total_price untuk item tersebut
      // Selalu pastikan quantity dan unit_price adalah angka
      const quantity = Number(items[index].quantity) || 0;
      const unitPrice = Number(items[index].unit_price) || 0;
      items[index].total_price = quantity * unitPrice;

      // 3. Hitung ulang summary (subtotal dan PPN) dari awal
      const subtotal = items.reduce(
        (sum, item) => sum + (Number(item.total_price) || 0),
        0
      );
      const ppnAmount = subtotal * 0.11;

      const summary = state.createQuotationInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // Action untuk mengubah nilai di dalam 'details'
    updateItemDetails: (state, action) => {
      const { itemIndex, field, value } = action.payload;

      const itemToUpdate = state.createQuotationInputBody.items[itemIndex];

      if (itemToUpdate && itemToUpdate.details) {
        itemToUpdate.details[field] = value;
      }
    },

    setClearCreateQuotationInputBody: (state, _) => {
      return {
        ...state,
        createQuotationInputBody: {
          quotation_id: "",
          title: "",
          version: 1,
          status: "Draft",
          issue_date: new Date(),
          valid_until_date: null,
          start_date: null,
          client: "",
          sales_person: "",
          department: "",
          items: [
            {
              item_type: "",
              description: "",
              quantity: 1,
              unit: "unit",
              unit_price: "",
              total_price: "",
              details: "",
            },
          ],
          summary: {
            subtotal: 0,
            discounts: [],
            taxes: [
              {
                tax_name: "PPN",
                rate_percent: 11,
                amount: 0,
              },
            ],
            grand_total: 0,
            currency: "IDR",
          },
          terms: {
            payment_schedule: [
              {
                description: "Pembayaran Penuh",
                percentage: 100,
                amount: 0,
                due_date: null,
              },
            ],
            scope_of_work: "",
            general_conditions: "",
          },
          notes: "",
          attachments: [],
        },
      };
    },
    setSelectedQuotation: (state, action) => {
      return {
        ...state,
        selectedQuotation: action.payload,
      };
    },
    setCreateQuotationInputBody: (state, action) => {
      return {
        ...state,
        createQuotationInputBody: action.payload,
      };
    },
    setEditQuotationInputBody: (state, action) => {
      return {
        ...state,
        editQuotationInputBody: action.payload,
      };
    },
    // note: clear state
    setClearGetQuotationObjectValue: (state, _) => {
      return {
        ...state,
        quotation_object_value: "",
      };
    },
    setClearGetQuotation: (state, _) => {
      return {
        ...state,
        getQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreateQuotation: (state, _) => {
      return {
        ...state,
        createQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdateQuotation: (state, _) => {
      return {
        ...state,
        updateQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteQuotation: (state, _) => {
      return {
        ...state,
        deleteQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllQuotationState: (state, _) => {
      return {
        ...state,
        // Get Quotation inisial state
        getQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create Quotation inisial state
        createQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Quotation inisial state
        updateQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Quotation inisial state
        deleteQuotation: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Quotation object value
        Quotation_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Quotation reducer
      .addCase(getQuotationThunk.pending, (state, _) => {
        return {
          ...state,
          getQuotation: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getQuotationThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getQuotation: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getQuotationThunk.rejected, (state, action) => {
        return {
          ...state,
          getQuotation: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create Quotation reducer
      .addCase(createQuotationThunk.pending, (state, _) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createQuotationThunk.fulfilled, (state, action) => {
        return {
          ...state,
          register: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createQuotationThunk.rejected, (state, action) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Quotation reducer
      .addCase(updateQuotationThunk.pending, (state, _) => {
        return {
          ...state,
          updateQuotation: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateQuotationThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateQuotation: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateQuotationThunk.rejected, (state, action) => {
        return {
          ...state,
          updateQuotation: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      .addCase(updateStatusQuotationThunk.pending, (state, _) => {
        return {
          ...state,
          updateStatusQuotation: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateStatusQuotationThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateStatusQuotation: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateStatusQuotationThunk.rejected, (state, action) => {
        return {
          ...state,
          updateStatusQuotation: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const quotationAction = {
  ...quotationSlice.actions,
  getQuotationThunk,
  createQuotationThunk,
  updateQuotationThunk,
  deleteQuotationThunk,
  updateStatusQuotationThunk,
};

export function selectQuotationObjectValue(state) {
  return state?.quotation?.quotation_object_value;
}

export function selectQuotation(state) {
  return state?.quotation;
}

export default quotationSlice.reducer;
